import { useMutation } from "@apollo/client"
import { yupResolver } from "@hookform/resolvers/yup"
import { useSSRDisabled } from "@skyltmax/ds9/hooks"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { type SubmitHandler, useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { graphql } from "@/api"
import FormForm from "@/common/components/form_form"
import FormInput from "@/common/components/form_input"
import Modal from "@/common/components/modal"
import yup from "@/common/yup"
import CheckIcon from "@/skylticons/checkmark_fill"

const subscribeDoc = graphql(`
  mutation NewsletterSubscribe($email: String!) {
    newsletterSubscribe(email: $email) {
      success
    }
  }
`)

export interface NewsletterInputs {
  [key: string]: string | undefined | null
  email: string
}

export const schema = yup.object().shape<NewsletterInputs>({
  email: yup.string().email().required(),
})

export interface NewsletterModalProps {
  privacyUrl: string
  modalTitle: string
  modalPromise: string
}

const NewsletterModal: React.FC<NewsletterModalProps> = ({ privacyUrl, modalTitle, modalPromise }) => {
  const { t } = useTranslation()
  const formMethods = useForm<NewsletterInputs>({ resolver: yupResolver(schema) })
  const { handleSubmit, reset } = formMethods
  const disabled = useSSRDisabled()

  const [open, setOpen] = useState(false)
  const [success, setSuccess] = useState(false)
  const closeTimer = useRef<number>()

  const toggleModal = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = undefined
    }

    reset()
    setSuccess(false)
    setOpen(v => !v)
  }, [reset])

  useEffect(() => {
    if (success && !closeTimer.current) {
      closeTimer.current = window.setTimeout(() => {
        setSuccess(false)

        closeTimer.current = undefined
      }, 10000)
    }
  }, [reset, success])

  const [mutation, mutationResult] = useMutation(subscribeDoc)

  const submit: SubmitHandler<NewsletterInputs> = useCallback(
    async (form, e) => {
      e?.preventDefault()

      await mutation({ variables: { email: form.email } })
      setOpen(false)
      setSuccess(true)
    },
    [mutation]
  )

  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <>
      <button
        id="newsletter_subscribe"
        className="button button-primary mb-0"
        onClick={toggleModal}
        data-gtm="newsletter-open"
        type="button"
        disabled={disabled}
      >
        {t("newsletter.button")}
      </button>

      <Modal width="600px" className="newsletter-modal" open={success} setOpen={setSuccess} padding="small">
        <Modal.Body className="success flex">
          <CheckIcon className="mr-2 h-8 w-8 text-success" />
          <div className="text">
            <h4 className="mb-2 pr-10">{t("newsletter.success_title")}</h4>
            <p className="paragraph-sm mb-0">{t("newsletter.success_text")}</p>
          </div>
          <Modal.CloseButton />
        </Modal.Body>
      </Modal>

      <Modal width="600px" className="newsletter-modal" open={open} setOpen={toggleModal} initialFocus={inputRef}>
        <Modal.Body>
          <FormForm id="update_form" onSubmit={handleSubmit(submit)} formMethods={formMethods}>
            <Modal.Title>{modalTitle}</Modal.Title>

            <p className="paragraph-sm">
              {modalPromise}
              &nbsp;
              <a href={privacyUrl} className="bold">
                {t("misc.read_more")}.
              </a>
            </p>
            <FormInput
              name="email"
              label={t("customer.email_address")}
              disabled={mutationResult.loading}
              ref={inputRef}
            />

            <button
              className="button button-primary"
              type="submit"
              disabled={mutationResult.loading}
              data-gtm="newsletter-submit"
            >
              {t("newsletter.button")}
            </button>
            <p className="paragraph-xs mb-0">
              <Trans i18nKey="newsletter.privacy_link" components={{ a: <a href={privacyUrl}>link</a> }} />
            </p>
          </FormForm>
          <Modal.CloseButton />
        </Modal.Body>
      </Modal>
    </>
  )
}

export default NewsletterModal
